// frontend/src/contexts/TechnicianAuthContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { technicianApiService } from '../services/technicianApiService';

const TechnicianAuthContext = createContext();

export const useTechnicianAuth = () => {
    const context = useContext(TechnicianAuthContext);
    if (!context) {
        throw new Error('useTechnicianAuth debe ser usado dentro de TechnicianAuthProvider');
    }
    return context;
};

export const TechnicianAuthProvider = ({ children }) => {
    const [technician, setTechnician] = useState(null);
    const [loading, setLoading] = useState(true);
    const [technicianToken, setTechnicianToken] = useState(localStorage.getItem('technicianToken')); // CORREGIDO: Renombrado a technicianToken

    useEffect(() => {
        if (technicianToken) {
            verifyToken();
        } else {
            setLoading(false);
        }
    }, [technicianToken]);

    const verifyToken = async () => {
        try {
            setLoading(true);
            const response = await technicianApiService.verifyToken();
            if (response.valid) {
                setTechnician(response.user);
                // Configurar el token en el servicio API
                technicianApiService.setToken(technicianToken);
            } else {
                logout();
            }
        } catch (error) {
            console.error('Error verificando token:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => { // CORREGIDO: Simplificado los parámetros
        try {
            setLoading(true);
            const response = await technicianApiService.login({ email, password });

            const { token: newToken, user } = response;

            // Guardar token y usuario
            localStorage.setItem('technicianToken', newToken);
            setTechnicianToken(newToken);
            setTechnician(user);

            // Configurar token en el servicio
            technicianApiService.setToken(newToken);

            return { success: true, user };
        } catch (error) {
            console.error('Error en login:', error);
            return {
                success: false,
                error: error.response?.data?.message || 'Error en el login'
            };
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            // Intentar hacer logout en el servidor
            if (technicianToken) {
                await technicianApiService.logout();
            }
        } catch (error) {
            console.error('Error en logout:', error);
        } finally {
            // Limpiar estado local
            localStorage.removeItem('technicianToken');
            localStorage.removeItem('activeWorkSession');
            setTechnicianToken(null);
            setTechnician(null);
            technicianApiService.setToken(null);
        }
    };

    const refreshToken = async () => {
        try {
            const response = await technicianApiService.refreshToken();
            const { token: newToken } = response;

            localStorage.setItem('technicianToken', newToken);
            setTechnicianToken(newToken);
            technicianApiService.setToken(newToken);

            return true;
        } catch (error) {
            console.error('Error refrescando token:', error);
            logout();
            return false;
        }
    };

    const updateTechnicianInfo = (updatedInfo) => {
        setTechnician(prev => ({ ...prev, ...updatedInfo }));
    };

    const isAuthenticated = () => {
        return !!(technicianToken && technician);
    };

    const hasPermission = (permission) => {
        // Esta función se puede expandir cuando implementes el sistema de permisos
        return true; // Por ahora, todos los técnicos tienen todos los permisos
    };

    const getWorkSession = () => {
        const session = localStorage.getItem('activeWorkSession');
        return session ? JSON.parse(session) : null;
    };

    const value = {
        technician,
        loading,
        technicianToken, // CORREGIDO: Exponer como technicianToken
        token: technicianToken, // AÑADIDO: Alias para compatibilidad
        login,
        logout,
        refreshToken,
        updateTechnicianInfo,
        isAuthenticated,
        hasPermission,
        getWorkSession
    };

    return (
        <TechnicianAuthContext.Provider value={value}>
            {children}
        </TechnicianAuthContext.Provider>
    );
};